/**
 * [TD-47.6a-ui Gap 47.6a-C + v1.1 Redline 4] ChatFilterDropdown - three-checkbox filter.
 *
 * v1.1 enum + chip labels (founder Q4 verdict A):
 *   • "manager" → "Chat"
 *   • "adam"    → "Adam"           (renamed from `draft_action`)
 *   • "system"  → "Round Starts"
 *
 * Default: all three on (no filtering applied).
 *
 * Note: this is a dropdown trigger button + popover. Click outside closes.
 */
import { useEffect, useRef, useState } from "react";
import { Filter } from "lucide-react";

const CHIPS = [
  { value: "manager", label: "Chat" },
  { value: "adam", label: "Adam" },
  { value: "system", label: "Round Starts" },
];

export default function ChatFilterDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (chip) => {
    const next = new Set(value);
    if (next.has(chip)) next.delete(chip);
    else next.add(chip);
    onChange?.(next);
  };

  const activeCount = value?.size ?? 0;
  const showDot = activeCount < CHIPS.length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-filter-dropdown-trigger"
        className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#252525] hover:bg-[#2E2E2E] text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
      >
        <Filter size={12} />
        <span className="font-display font-bold text-[10px] uppercase tracking-wider">
          Filter
        </span>
        {showDot ? (
          <span
            className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]"
            data-testid="chat-filter-active-dot"
          />
        ) : null}
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full mt-1 z-20 w-40 bg-[#1C1C1C] border border-[#252525] rounded-lg shadow-lg p-2"
          data-testid="chat-filter-popover"
        >
          {CHIPS.map(({ value: v, label }) => {
            const isOn = value?.has(v);
            return (
              <label
                key={v}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#252525] cursor-pointer transition-colors"
                data-testid={`chat-filter-chip-${v}`}
              >
                <input
                  type="checkbox"
                  checked={!!isOn}
                  onChange={() => toggle(v)}
                  className="accent-[#FF6B00]"
                />
                <span className="font-body text-xs text-[#F5F5F5]">{label}</span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
